import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, CheckCircle2, Award, Loader2 } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import { fetchCaseStudy } from '../lib/api';

const CaseStudyDetail = () => {
  const { id } = useParams();
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCaseStudy(id)
      .then(d => { setCs(d); setLoading(false); })
      .catch(err => { setError(err?.response?.data?.detail || err?.message || 'Not found'); setLoading(false); });
  }, [id]);

  const onPrint = () => window.print();

  if (loading) return (
    <div className="max-w-[1320px] mx-auto px-6 py-20 flex items-center gap-2 text-slate-400">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading case study…
    </div>
  );

  if (error || !cs) return (
    <div className="max-w-[1320px] mx-auto px-6 py-20 text-slate-300">
      <div className="text-red-400 mb-4">{error || 'Case study not found'}</div>
      <Link to="/experience" className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200"><ArrowLeft className="w-4 h-4" /> Back to Experience</Link>
    </div>
  );

  return (
    <div>
      <section className="pt-16 pb-8 relative overflow-hidden print:pt-4">
        <div className="absolute inset-0 bg-grid opacity-40 print:hidden" />
        <div className="relative max-w-[1100px] mx-auto px-6">
          <div className="flex items-center justify-between mb-8 print:hidden">
            <Link to="/experience" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-orange-300 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Experience
            </Link>
            <button onClick={onPrint} className="inline-flex items-center gap-2 border border-white/15 hover:border-orange-400/60 text-slate-200 hover:text-orange-300 font-medium text-sm px-4 py-2 rounded-md transition-colors">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>

          <SectionLabel icon={Award}>OPERATIONAL BACKGROUND WRITE-UP</SectionLabel>
          <div className="mt-3 text-[11px] font-mono tracking-[0.2em] text-orange-300 px-2.5 py-1 rounded bg-orange-500/10 border border-orange-500/20 inline-block">{cs.tag}</div>
          <h1 className="mt-4 font-serif text-white text-4xl md:text-5xl leading-[1.1] tracking-tight max-w-3xl print:text-3xl">{cs.title}</h1>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1100px] mx-auto px-6 grid md:grid-cols-4 gap-4">
          {(cs.metrics || []).map(m => (
            <div key={m.label} className="rounded-lg border border-white/8 bg-white/[0.02] p-5 print:bg-white print:border-slate-300">
              <div className="font-serif text-2xl text-orange-400 print:text-slate-900">{m.value}</div>
              <div className="text-xs font-mono tracking-wider text-slate-400 mt-1 print:text-slate-600">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-6">
        <div className="max-w-[1100px] mx-auto px-6 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-7 print:bg-white print:border-slate-300">
            <div className="text-[11px] font-mono tracking-[0.2em] text-slate-500 print:text-slate-600">BACKGROUND</div>
            <p className="mt-3 text-slate-200 leading-relaxed print:text-slate-800">{cs.background}</p>
            <div className="mt-6 rounded-lg border-l-2 border-emerald-400/60 bg-emerald-400/[0.06] p-4 print:bg-emerald-50 print:border-emerald-500">
              <div className="text-[11px] font-mono tracking-[0.2em] text-emerald-300 print:text-emerald-700">RESULT / GOVERNANCE IMPACT</div>
              <p className="mt-2 text-slate-100 leading-relaxed print:text-slate-800">{cs.result}</p>
            </div>
          </div>
          <div className="rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-7 print:bg-white print:border-slate-300">
            <div className="text-[11px] font-mono tracking-[0.2em] text-orange-300 mb-4 print:text-slate-700">ENGAGEMENT HIGHLIGHTS</div>
            <ul className="space-y-3">
              {(cs.highlights || []).map(h => (
                <li key={h} className="flex items-start gap-2 text-sm text-slate-200 print:text-slate-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 print:text-emerald-600 flex-shrink-0" /> {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-10 print:hidden">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="rounded-xl border border-white/8 bg-[#0f1a2f]/60 p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="font-serif text-white text-xl">Discuss a similar engagement</div>
              <div className="text-sm text-slate-400 mt-1">Advisory availability across state road authorities and Tier-1 consultancies.</div>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors">
              Request Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
